'use client';

import { useState, useEffect, useRef } from 'react';
import { useSocketManagerContext } from '../providers/context';
import PricesWebSocketManager from '../library/prices-manager';
import USPricesWebSocketManager from '../library/us-prices-manager';
import NatsManager from '../library/nats-manager';
import type { LivePriceData } from '../types';
import {
  parseWebSocketMessage,
  parseNatsMessage,
  getEffectiveSourceMultiple,
  isUSStocksMultipleOptions,
  type IndianStocksMultipleOptions,
  type USStocksMultipleOptions,
  type EffectiveSource,
} from './use-live-price-utils';

/**
 * Options for useLivePriceMultiple hook
 */
export type UseLivePriceMultipleOptions =
  | IndianStocksMultipleOptions
  | USStocksMultipleOptions;

/**
 * Hook to subscribe to multiple live price updates.
 *
 * @example
 * // Indian stocks - NATS
 * const prices = useLivePriceMultiple({
 *   natsSocketPaths: ['prices.NSE.RELIANCE', 'prices.NSE.TCS'],
 * });
 *
 * @example
 * // US stocks
 * const prices = useLivePriceMultiple({
 *   usWebSocketPaths: ['AAPL:NASDAQ:US', 'GOOGL:NASDAQ:US'],
 * });
 */
export function useLivePriceMultiple(
  options: UseLivePriceMultipleOptions,
): Record<string, LivePriceData> {
  const { enabled = true } = options;

  const isUSStocks = isUSStocksMultipleOptions(options);

  // Extract paths based on type
  const webSocketPaths = isUSStocks ? undefined : options.webSocketPaths;
  const natsSocketPaths = isUSStocks ? undefined : options.natsSocketPaths;
  const usWebSocketPaths = isUSStocks ? options.usWebSocketPaths : undefined;
  const source = isUSStocks ? 'websocket' : (options.source ?? 'websocket');

  const [pricesData, setPricesData] = useState<Record<string, LivePriceData>>(
    {},
  );
  const context = useSocketManagerContext();
  const unsubscribesRef = useRef<Map<string, () => void>>(new Map());

  const effectiveSource: EffectiveSource = getEffectiveSourceMultiple(
    isUSStocks,
    webSocketPaths,
    natsSocketPaths,
    source,
  );

  // Determine which paths to use
  let socketPaths: string[] = [];
  if (effectiveSource === 'us') {
    socketPaths = usWebSocketPaths || [];
  } else if (effectiveSource === 'websocket') {
    socketPaths = webSocketPaths || [];
  } else {
    socketPaths = natsSocketPaths || [];
  }

  useEffect(() => {
    unsubscribesRef.current.forEach((unsub) => unsub());
    unsubscribesRef.current.clear();

    if (!enabled || socketPaths.length === 0) {
      setPricesData({});
      return;
    }

    // US Stocks WebSocket subscription
    if (effectiveSource === 'us') {
      if (!context.isUSPricesConnected) return;

      const manager = USPricesWebSocketManager.getUSPricesWSInstance();
      if (!manager) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[useLivePriceMultiple] USPricesWebSocketManager not initialized',
          );
        }
        return;
      }

      socketPaths.forEach((path) => {
        if (!path) return;
        try {
          const unsub = manager.subscribe(path, (message: unknown) => {
            const parsed = parseWebSocketMessage(message);
            if (parsed) {
              setPricesData((prev) => ({ ...prev, [path]: parsed }));
            }
          });
          unsubscribesRef.current.set(path, unsub);
        } catch (error) {
          console.error(
            '[useLivePriceMultiple] Error subscribing to US prices:',
            path,
            error,
          );
        }
      });
    }

    // Indian Stocks WebSocket subscription
    if (effectiveSource === 'websocket') {
      if (!context.isPricesConnected) return;

      const manager = PricesWebSocketManager.getPricesWSInstance();
      if (!manager) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[useLivePriceMultiple] PricesWebSocketManager not initialized',
          );
        }
        return;
      }

      socketPaths.forEach((path) => {
        if (!path) return;
        try {
          const unsub = manager.subscribe(path, (message: unknown) => {
            const parsed = parseWebSocketMessage(message);
            if (parsed) {
              setPricesData((prev) => ({ ...prev, [path]: parsed }));
            }
          });
          unsubscribesRef.current.set(path, unsub);
        } catch (error) {
          console.error(
            '[useLivePriceMultiple] Error subscribing:',
            path,
            error,
          );
        }
      });
    }

    // NATS subscription
    if (effectiveSource === 'nats') {
      if (!context.isNatsConnected) return;

      const natsManager = NatsManager.getInstance();

      socketPaths.forEach((path) => {
        if (!path) return;
        try {
          const unsub = natsManager.subscribe(path, (data, error) => {
            if (error) {
              console.error('[useLivePriceMultiple] NATS error:', error);
              return;
            }
            setPricesData((prev) => ({
              ...prev,
              [path]: parseNatsMessage(data),
            }));
          });
          unsubscribesRef.current.set(path, unsub);
        } catch (error) {
          console.error(
            '[useLivePriceMultiple] Error subscribing:',
            path,
            error,
          );
        }
      });
    }

    return () => {
      unsubscribesRef.current.forEach((unsub) => unsub());
      unsubscribesRef.current.clear();
    };
  }, [
    enabled,
    socketPaths.join(','),
    effectiveSource,
    context.isPricesConnected,
    context.isUSPricesConnected,
    context.isNatsConnected,
  ]);

  return pricesData;
}

export default useLivePriceMultiple;
